import { sortBy } from 'lodash';
import { LabeledDataPoint, PreparedDataPoint } from '../data';

export type DataPoint = {
  label: string;
  value: number;
  detail: string | null;
};

type Entry<T> = DataPoint & {
  items: T[];
};

export type SortType = 'DESC' | 'ASC' | 'NONE';
export type SortBy = 'VALUE' | 'COUNT';

export function combine<T>(
  map: Map<string, T[]>,
  max: number,
  reducer: (items: T[]) => number,
  sorted: SortType,
  sortBy: SortBy = 'VALUE',
  detailReducer: ((items: T[]) => string) | null = null,
): DataPoint[] {
  let filteredEntries: Entry<T>[] = Array.from(map.entries())
    .map(([label, items]) => ({
      label,
      items,
      value: reducer(items),
      detail: detailReducer && detailReducer(items),
    }))
    .filter(({ value }) => !isNaN(value) && value !== Infinity);
  if (sorted != 'NONE') {
    filteredEntries = filteredEntries.sort(
      (a, b) =>
        (sorted == 'DESC' ? 1 : -1) *
        (sortBy === 'VALUE'
          ? b.value - a.value
          : b.items.length - a.items.length),
    );
  }
  if (filteredEntries.length > max) {
    const showCount = max - 1;
    const items = Array.prototype.concat.apply(
      [],
      filteredEntries.slice(showCount).map(({ items }) => items),
    );
    return [
      ...filteredEntries.slice(0, showCount),
      {
        label: 'Other',
        value: reducer(items),
        detail: detailReducer && detailReducer(items),
      },
    ];
  } else {
    return filteredEntries;
  }
}

export function combinePrepared<T>(
  collection: PreparedDataPoint<T>[],
  max: number,
  calculateMetric: (metricData: T) => number,
  reduceTail: (items: T[]) => T,
  sorted: SortType,
): LabeledDataPoint[] {
  const sortedFilteredItems = sortBy(collection, ({ metricData }) => {
    switch (sorted) {
      case 'DESC':
        return calculateMetric(metricData) * -1;
      case 'ASC':
        return calculateMetric(metricData);
      case 'NONE':
        return 0;
    }
  }).filter(({ metricData }) => {
    const value = calculateMetric(metricData);
    return !isNaN(value) && value !== Infinity;
  });
  if (sortedFilteredItems.length > max) {
    const showCount = max - 1;
    return [
      ...sortedFilteredItems
        .slice(0, showCount)
        .map(({ label, metricData }) => ({
          label,
          value: calculateMetric(metricData),
        })),
      {
        label: 'Other',
        value: calculateMetric(
          reduceTail(
            sortedFilteredItems
              .slice(showCount)
              .map(({ metricData }) => metricData),
          ),
        ),
      },
    ];
  } else {
    return sortedFilteredItems.map(({ label, metricData }) => ({
      label,
      value: calculateMetric(metricData),
    }));
  }
}
